import type { Member, ScheduleEvent, Settings } from "./types";

export const HOURS = [10, 11, 12, 13, 14, 15, 16] as const;
export const WEEKDAYS_JA = ["日", "月", "火", "水", "木", "金", "土"] as const;
export const UNASSIGNED_MEMBER_ID = "__unassigned__";

export function dateString(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export type HiddenReason = "unassigned" | "team" | "duration";

export const HIDDEN_REASON_LABELS: Record<HiddenReason, string> = {
  unassigned: "未割り当て",
  team: "チーム予定フィルター",
  duration: "最小表示時間",
};

/** "YYYY-MM-DDTHH:mm" を JST の壁時計として解釈する。ブラウザの TZ や DST に依存させない。 */
function jstTime(value: string): number {
  if (value.length < 16 || value.charAt(10) !== "T") return Number.NaN;
  return new Date(`${value.slice(0, 16)}:00+09:00`).getTime();
}

/**
 * 時間指定の予定の長さ（時間）。終日予定と、日時を読めない予定は undefined。
 * タイムラインの最小表示時間と Outlook 候補の最小時間は必ずこの関数で測る。
 */
export function eventDurationHours(
  event: Pick<ScheduleEvent, "start" | "end" | "allDay">,
): number | undefined {
  if (event.allDay) return undefined;
  const duration = jstTime(event.end) - jstTime(event.start);
  return Number.isFinite(duration) ? duration / 3_600_000 : undefined;
}

export function hiddenReason(event: ScheduleEvent, settings: Settings): HiddenReason | undefined {
  if (event.memberIds.length === 0 && !settings.showUnassignedEvents) return "unassigned";
  if (event.memberIds.length === 0) return undefined;
  if (
    event.teamAddresses?.some((address) =>
      settings.hiddenTeamAddresses.includes(address.toLowerCase()),
    )
  ) {
    return "team";
  }
  if (event.allDay) return undefined;
  // タイムラインは Notion のデータだけを描画するため、取り込み元（Source）では区別しない。
  // 日時を読めない予定は長さ不明として隠す。
  const hours = eventDurationHours(event);
  return hours !== undefined && hours >= settings.minDurationHours ? undefined : "duration";
}

export function eventVisible(event: ScheduleEvent, settings: Settings): boolean {
  return hiddenReason(event, settings) === undefined;
}

/** 取得済みだが表示条件で隠れている件数を理由ごとにまとめる。 */
export function hiddenEventSummary(
  events: ScheduleEvent[],
  settings: Settings,
): { reason: HiddenReason; count: number }[] {
  const counts = new Map<HiddenReason, number>();
  for (const event of events) {
    const reason = hiddenReason(event, settings);
    if (reason) counts.set(reason, (counts.get(reason) ?? 0) + 1);
  }
  return (Object.keys(HIDDEN_REASON_LABELS) as HiddenReason[])
    .filter((reason) => counts.has(reason))
    .map((reason) => ({ reason, count: counts.get(reason) ?? 0 }));
}

export function dayList(settings: Settings): Date[] {
  const days: Date[] = [];
  const current = new Date(`${settings.from}T00:00:00`);
  const end = new Date(`${settings.to}T00:00:00`);
  while (current <= end) {
    if (settings.showWeekends || (current.getDay() !== 0 && current.getDay() !== 6)) {
      days.push(new Date(current));
    }
    current.setDate(current.getDate() + 1);
  }
  return days;
}

export function eventsAtSlot(
  events: ScheduleEvent[],
  memberId: string,
  day: Date,
  hour: number,
  settings: Settings,
): ScheduleEvent[] {
  const date = dateString(day);
  const slotStart = new Date(`${date}T${String(hour).padStart(2, "0")}:00`);
  const slotEnd = new Date(slotStart.getTime() + 3_600_000);
  return events.filter((event) => {
    const assignedToRow =
      memberId === UNASSIGNED_MEMBER_ID
        ? event.memberIds.length === 0
        : event.memberIds.includes(memberId);
    if (!assignedToRow || !eventVisible(event, settings)) return false;
    if (event.allDay) return date >= event.start.slice(0, 10) && date <= event.end.slice(0, 10);
    return new Date(event.start) < slotEnd && new Date(event.end) > slotStart;
  });
}

export function eventSetKey(events: ScheduleEvent[]): string {
  return events.map((event) => event.id).sort().join("|");
}

export function colorAssignment(events: ScheduleEvent[], settings: Settings): Record<string, number> {
  const sorted = events
    .filter((event) => !event.live && eventVisible(event, settings))
    .slice()
    .sort((a, b) => String(a.start).localeCompare(String(b.start)));
  return Object.fromEntries(sorted.map((event, index) => [event.id, (index % 8) + 1]));
}

/**
 * セル上のラベルが次の予定に被らずに使えるコマ数。
 * 同じ予定が続くコマと、その後に続く空きコマ（Notion 登録済み予定が無いコマ）は
 * 同じ日の範囲内で使ってよい。別の予定が始まるコマ、または日の終わりで止める。
 */
export function labelSlotSpan(dayCells: readonly Pick<GridCell, "key">[], hourIndex: number): number {
  const key = dayCells[hourIndex]?.key;
  if (!key) return 0;
  let span = 0;
  for (let index = hourIndex; index < dayCells.length; index += 1) {
    const cellKey = dayCells[index]?.key ?? "";
    if (cellKey !== key && cellKey !== "") break;
    span += 1;
  }
  return span;
}

/**
 * ある担当者・ある日のコマ列に含まれる Notion 登録済み予定を、重複を除いて返す。
 * 編集モーダルは「その日に重なっている予定」を全部見せたいので、クリックしたコマだけでなく
 * 同じ日の全コマから集める。終日予定を先に、あとは開始日時順に並べる。
 */
export function dayEvents(dayCells: readonly Pick<GridCell, "events">[]): ScheduleEvent[] {
  const seen = new Map<string, ScheduleEvent>();
  for (const cell of dayCells) {
    for (const event of cell.events) {
      if (!seen.has(event.id)) seen.set(event.id, event);
    }
  }
  return [...seen.values()].sort((a, b) => {
    if (a.allDay !== b.allDay) return a.allDay ? -1 : 1;
    return a.start.localeCompare(b.start) || a.id.localeCompare(b.id);
  });
}

export interface GridCell {
  key: string;
  /** Notion 登録済みの予定。色付きのセルと結合キーはこちらだけで決める。 */
  events: ScheduleEvent[];
  /** Notion 未登録の生 Outlook 予定。件数が多いので帯だけで表す。 */
  liveEvents: ScheduleEvent[];
}

export function buildGridModel(
  events: ScheduleEvent[],
  members: Member[],
  days: Date[],
  settings: Settings,
): GridCell[][][] {
  return members.map((member) =>
    days.map((day) =>
      HOURS.map((hour) => {
        const slotEvents = eventsAtSlot(events, member.id, day, hour, settings);
        const registered = slotEvents.filter((event) => !event.live);
        return {
          key: eventSetKey(registered),
          events: registered,
          liveEvents: slotEvents.filter((event) => event.live),
        };
      }),
    ),
  );
}
